'use client'

import { useCallback, useRef, useState } from 'react'

import { MaintenanceModal, readMaintenance, useMaintenance } from '@/components/ui/maintenance'

import { useDestination } from '../../destination/context/DestinationContext'
import BookingSection from './components/BookingSection'
import ConfirmSection from './components/ConfirmSection'
import GiftDetailsCard from './components/GiftDetailsCard'
import VerifySection from './components/VerifySection'
import type { BookingFormValues } from './hooks/redeem.schema'
import { useRedeem } from './hooks/useRedeem'
import type { IRedeemDeeplink, IRedeemQueryPrefill } from './types/schema'

interface Props {
  locale: string
  initialDeeplink: IRedeemDeeplink | null
  initialPrefill: IRedeemQueryPrefill | null
}

/**
 * One scrolling page with progressive disclosure. The booking block is sealed until the gift
 * verifies, then the confirmation replaces it in place — `stage` only decides what is unlocked.
 */
export default function RedeemClient({ locale, initialDeeplink, initialPrefill }: Props) {
  const { data } = useDestination()
  const maintenance = readMaintenance(data?.common)
  const blocked = maintenance?.status === true

  const liveMaintenance = useMaintenance()
  const maintenanceCheck = liveMaintenance.check

  const redeem = useRedeem({
    locale,
    initialDeeplink: blocked ? null : initialDeeplink,
    initialPrefill
  })

  const verified = redeem.stage !== 'verify' && Boolean(redeem.giftDetail)
  const showConfirm = redeem.stage === 'confirm' && Boolean(redeem.bookingDetail)

  const unlockedRef = useRef<HTMLDivElement>(null)

  const [verifyEpoch, setVerifyEpoch] = useState(0)

  const scrollToUnlocked = useCallback(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    unlockedRef.current?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' })
  }, [])

  const redeemReset = redeem.reset
  const runBooking = redeem.runBooking

  const startOver = useCallback(() => {
    redeemReset()
    setVerifyEpoch((n) => n + 1)

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' })
  }, [redeemReset])


  const submitBooking = useCallback(
    async (values: BookingFormValues, cityId: number) => {
      if (await maintenanceCheck()) return false

      return runBooking(values, cityId)
    },
    [maintenanceCheck, runBooking]
  )

  return (
    <div className={`bg-theme-light ${verified ? 'pb-12 md:pb-40' : 'pb-12'}`}>
      <VerifySection
        key={verifyEpoch}
        blocked={blocked}
        locked={verified}
        isVerifying={redeem.isVerifying}
        error={verified ? null : redeem.error}
        usedBooking={redeem.usedBooking}
        verifiedGift={verified ? redeem.giftDetail : null}
        initialDeeplink={verifyEpoch === 0 ? initialDeeplink : null}
        initialPrefill={initialPrefill}
        onVerify={redeem.runVerify}
        onVerified={scrollToUnlocked}
      />

      <div
        ref={unlockedRef}
        inert={!verified}
        aria-hidden={!verified}
        className={`scroll-mt-32 md:scroll-mt-24 ${verified ? '' : 'select-none opacity-30'}`}>
        {redeem.giftDetail && <GiftDetailsCard gift={redeem.giftDetail} />}

        {showConfirm && redeem.bookingDetail ? (
          <ConfirmSection detail={redeem.bookingDetail} onStartOver={startOver} />
        ) : (
          redeem.giftDetail &&
          redeem.filter && (
            <BookingSection
              blocked={blocked}
              gift={redeem.giftDetail}
              filter={redeem.filter}
              prefill={initialPrefill}
              recommend={redeem.recommend}
              error={redeem.error}
              isBooking={redeem.isBooking}
              isCheckingAvailability={redeem.isCheckingAvailability}
              onSubmitBooking={submitBooking}
              onCheckAvailability={redeem.runAvailabilityCheck}
              onRecommendTaken={redeem.clearRecommend}
              onStartOver={startOver}
            />
          )
        )}
      </div>

      <MaintenanceModal
        isOpen={blocked || liveMaintenance.isBlocked}
        message={blocked ? maintenance?.message : liveMaintenance.message}
        onClose={blocked ? undefined : liveMaintenance.close}
      />
    </div>
  )
}
